/**
 * @fileoverview Guided Filter 执行和工具模块
 * 执行算法、创建缓冲区、性能测试等
 * 
 * @author 织
 * @version 1.0.0
 */

import { validateGuidedFilterParams } from './guided-filter-params.js';
import { 
  createGuidedFilterPipeline, 
  createDetailExtractionPipeline,
  createGuidedFilterBindGroupLayout,
  createDetailExtractionBindGroupLayout,
  createGuidedFilterBindGroup,
  createDetailExtractionBindGroup
} from './guided-filter-pipeline.js';

/**
 * 创建uniform缓冲区
 * @param {GPUDevice} device - WebGPU设备
 * @param {GuidedFilterParams} params - 参数
 * @param {number} width - 纹理宽度
 * @param {number} height - 纹理高度
 * @returns {GPUBuffer} uniform缓冲区
 */
export function createUniformBuffer(device, params, width, height) {
  const bufferData = new ArrayBuffer(20); // 5个值：2个u32 + 2个f32 + 1个u32
  const view = new DataView(bufferData);
  
  // 写入参数
  view.setUint32(0, width, true);      // width
  view.setUint32(4, height, true);     // height
  view.setFloat32(8, params.sigma, true);   // sigma
  view.setFloat32(12, params.epsilon, true); // epsilon
  view.setUint32(16, params.radius, true);  // radius
  
  const uniformBuffer = device.createBuffer({
    size: 20,
    usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    label: 'GuidedFilter Uniform Buffer'
  });
  
  device.queue.writeBuffer(uniformBuffer, 0, bufferData);
  
  return uniformBuffer;
}

/**
 * 创建细节提取uniform缓冲区
 * @param {GPUDevice} device - WebGPU设备
 * @param {number} width - 纹理宽度
 * @param {number} height - 纹理高度
 * @returns {GPUBuffer} uniform缓冲区
 */
export function createDetailUniformBuffer(device, width, height) {
  const bufferData = new ArrayBuffer(8); // 2个u32值
  const view = new DataView(bufferData);
  
  // 写入参数
  view.setUint32(0, width, true);      // width
  view.setUint32(4, height, true);     // height
  
  const uniformBuffer = device.createBuffer({
    size: 8,
    usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    label: 'Detail Extraction Uniform Buffer'
  });
  
  device.queue.writeBuffer(uniformBuffer, 0, bufferData);
  
  return uniformBuffer;
}

/**
 * 执行Guided Filter处理
 * @param {GPUDevice} device - WebGPU设备
 * @param {GPUTexture} inputTexture - 输入纹理
 * @param {GuidedFilterParams} params - 算法参数
 * @param {number} width - 纹理宽度
 * @param {number} height - 纹理高度
 * @returns {Promise<{baseTexture: GPUTexture, detailTexture: GPUTexture}>} 处理结果
 */
export async function executeGuidedFilter(device, inputTexture, params, width, height) {
  // 验证参数
  if (!validateGuidedFilterParams(params)) {
    throw new Error('Invalid Guided Filter parameters');
  }
  
  // 使用传入的尺寸参数，而不是尝试从纹理获取
  
  // 创建输出纹理
  const baseTexture = device.createTexture({
    size: { width, height },
    format: 'rgba32float',
    usage: GPUTextureUsage.STORAGE_BINDING | GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_SRC,
    label: 'Base Texture'
  });
  
  const detailTexture = device.createTexture({
    size: { width, height },
    format: 'rgba32float',
    usage: GPUTextureUsage.STORAGE_BINDING | GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_SRC,
    label: 'Detail Texture'
  });
  
  // 创建uniform缓冲区
  const guidedUniformBuffer = createUniformBuffer(device, params, width, height);
  const detailUniformBuffer = createDetailUniformBuffer(device, width, height);
  
  // 创建BindGroup布局
  const guidedBindGroupLayout = createGuidedFilterBindGroupLayout(device);
  const detailBindGroupLayout = createDetailExtractionBindGroupLayout(device);
  
  // 创建管线
  const guidedPipeline = createGuidedFilterPipeline(device, params, guidedBindGroupLayout);
  const detailPipeline = createDetailExtractionPipeline(device, detailBindGroupLayout);
  
  // 创建BindGroup
  const guidedBindGroup = createGuidedFilterBindGroup(device, guidedBindGroupLayout, {
    inputTexture,
    outputTexture: baseTexture,
    uniformBuffer: guidedUniformBuffer
  });
  
  const detailBindGroup = createDetailExtractionBindGroup(device, detailBindGroupLayout, {
    inputTexture,
    baseTexture,
    outputTexture: detailTexture,
    uniformBuffer: detailUniformBuffer
  });
  
  
  // 执行计算
  const encoder = device.createCommandEncoder({ label: 'Guided Filter Encoder' });
  
  // 1. 执行Guided Filter
  const guidedPass = encoder.beginComputePass({ label: 'Guided Filter Pass' });
  guidedPass.setPipeline(guidedPipeline);
  guidedPass.setBindGroup(0, guidedBindGroup);
  
  // 修复工作组分派：确保覆盖整个图像，避免边界间隙
  const dispatchX = Math.ceil(width / 16);
  const dispatchY = Math.ceil(height / 16);
  
  guidedPass.dispatchWorkgroups(dispatchX, dispatchY);
  guidedPass.end();
  
  // 提交Guided Filter命令并等待完成
  device.queue.submit([encoder.finish()]);
  await device.queue.onSubmittedWorkDone();
  
  // 2. 创建新的command encoder执行Detail Extraction
  const detailEncoder = device.createCommandEncoder({ label: 'Detail Extraction Encoder' });
  const detailPass = detailEncoder.beginComputePass({ label: 'Detail Extraction Pass' });
  detailPass.setPipeline(detailPipeline);
  detailPass.setBindGroup(0, detailBindGroup);
  
  // 使用与Guided Filter相同的分派策略
  detailPass.dispatchWorkgroups(dispatchX, dispatchY);
  detailPass.end();
  
  
  // 提交Detail Extraction命令
  device.queue.submit([detailEncoder.finish()]);
  
  // 等待Detail Extraction完成并检查是否有错误
  try {
    await device.queue.onSubmittedWorkDone();
    console.log('Detail Extraction completed successfully');
  } catch (error) {
    console.error('Detail Extraction failed:', error);
    throw error;
  }
  
  return {
    baseTexture,
    detailTexture
  };
}

/**
 * 获取纹理尺寸
 * @param {GPUTexture} texture - 纹理对象
 * @returns {{width: number, height: number}} 纹理尺寸
 */
function getTextureSize(texture) {
  // WebGPU纹理对象没有直接的width/height属性
  // 需要从创建时的描述符中获取，或者传入尺寸参数
  // 这里我们通过纹理的描述符来获取尺寸
  
  // 尝试从纹理的描述符获取尺寸
  if (texture && texture.dimension === '2d') {
    // 对于2D纹理，我们可以通过纹理的描述符来获取尺寸
    // 但由于WebGPU API限制，我们需要其他方式
    
    // 临时解决方案：从纹理的创建参数中获取
    // 这需要修改纹理创建过程，传递尺寸信息
    console.warn('无法直接获取纹理尺寸，需要从创建参数中获取');
  }
  
  // 临时返回一个合理的默认值
  // 实际应用中应该从纹理创建时保存的尺寸信息中获取
  return { width: 1024, height: 768 };
}

/**
 * 性能测试函数
 * @param {GPUDevice} device - WebGPU设备
 * @param {GuidedFilterParams} params - 算法参数
 * @param {number[][]} resolutions - 测试分辨率列表
 * @returns {Promise<Object>} 性能测试结果
 */
export async function benchmarkGuidedFilter(device, params, resolutions) {
  const results = {
    resolutions: {},
    averageTime: 0,
    totalTests: 0
  };
  
      for (const [width, height] of resolutions) {
      // 创建测试纹理
      const { texture: testTexture } = createTestTexture(device, width, height);
      
      const startTime = performance.now();
      await executeGuidedFilter(device, testTexture, params, width, height);
      const endTime = performance.now();
    
    const time = endTime - startTime;
    results.resolutions[`${width}x${height}`] = time;
    results.averageTime += time;
    results.totalTests++;
  }
  
  results.averageTime /= results.totalTests;
  return results;
}

/**
 * 创建测试纹理
 * @param {GPUDevice} device - WebGPU设备
 * @param {number} width - 宽度
 * @param {number} height - 高度
 * @returns {{texture: GPUTexture, width: number, height: number}} 测试纹理和尺寸
 */
function createTestTexture(device, width, height) {
  // 创建测试图像数据
  const floatData = new Float32Array(width * height * 4);
  
  // 生成测试图案
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const index = (y * width + x) * 4;
      
      // 创建边缘测试图案
      const edge = Math.sin(x * 0.1) * Math.cos(y * 0.1);
      const noise = Math.random() * 0.1;
      
      floatData[index] = (edge + noise);     // R
      floatData[index + 1] = (edge + noise); // G
      floatData[index + 2] = (edge + noise); // B
      floatData[index + 3] = 1.0;            // A
    }
  }
  
  // 创建纹理
  const texture = device.createTexture({
    size: { width, height },
    format: 'rgba32float',
    usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST,
    label: 'Test Texture'
  });
  
  // 上传数据
  device.queue.writeTexture(
    { texture },
    floatData,
    { bytesPerRow: width * 16 }, // rgba32float每像素16字节
    { width, height }
  );
  
  return { texture, width, height };
} 